import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CircleNotch, X } from "@phosphor-icons/react";
import { Reveal, Overline } from "./primitives";
import { useSelection } from "./SelectionContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLIENT_TYPES = ["Hotel Group", "Restaurant", "Corporate", "Members' Club", "Other"];
const VOLUMES = ["50–200", "200–1,000", "1,000+", "Not sure yet"];

export const Enquiry = () => {
  const { items, remove, clear } = useSelection();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", enquiry_type: "", volume: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, items: items.map((i) => `${i.name} (${i.range})`) });
      toast.success("Thank you — your selection is on its way. We'll respond within two working days.");
      setForm({ name: "", company: "", email: "", phone: "", enquiry_type: "", volume: "", message: "" });
      clear();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="relative z-10 px-6 md:px-12 lg:px-16 py-28 md:py-40 border-t border-black/10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Reveal><Overline>Request a Selection</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Let's build<br /><span className="italic text-[var(--brass)]">your order.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-[var(--text-secondary)] text-lg leading-relaxed max-w-sm">
              Send us your selection, volumes and theme. We reply to every trade enquiry within two working days with a tailored quote and samples.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 space-y-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--text-muted)]">
              <p>asadsadaan29@gmail.com</p>
              <p>07849 049356</p>
              <p>www.emporiumlondon.co.uk</p>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-6 md:col-start-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} data-testid="enquiry-form" className="space-y-10">
              {items.length > 0 && (
                <div data-testid="selection-summary" className="border border-black/15 p-5 bg-[var(--bg-secondary)]">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)] mb-3">Your selection · {items.length}</p>
                  <div className="flex flex-wrap gap-2">
                    {items.map((i, idx) => (
                      <span key={`${i.id}-${idx}`} data-testid={`chip-${i.id}`} className="inline-flex items-center gap-2 border border-black/20 px-3 py-1.5 font-mono text-[10px] tracking-[0.12em] uppercase text-[var(--text-secondary)]">
                        {i.name}
                        <button type="button" onClick={() => remove(i.id)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={12} weight="bold" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {[
                { name: "name", label: "Your name", type: "text" },
                { name: "company", label: "Company / group", type: "text" },
                { name: "email", label: "Email address", type: "email" },
                { name: "phone", label: "Phone (optional)", type: "text" },
              ].map((f) => (
                <div key={f.name}>
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">{f.label}</label>
                  <input
                    data-testid={`input-${f.name}`}
                    type={f.type}
                    value={form[f.name]}
                    onChange={(e) => set(f.name, e.target.value)}
                    className="mt-2 w-full bg-transparent border-b border-black/20 py-3 text-lg text-[var(--text-primary)] focus:border-[var(--text-primary)] outline-none transition-colors duration-300"
                  />
                </div>
              ))}

              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">Client type</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {CLIENT_TYPES.map((t) => (
                    <button key={t} type="button" data-testid={`ctype-${t}`} onClick={() => set("enquiry_type", t)}
                      className={`border px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${form.enquiry_type === t ? "bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)]" : "border-black/20 text-[var(--text-secondary)] hover:border-black/50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">Estimated volume (units)</label>
                <div className="mt-3 flex flex-wrap gap-2">
                  {VOLUMES.map((t) => (
                    <button key={t} type="button" data-testid={`volume-${t}`} onClick={() => set("volume", t)}
                      className={`border px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase transition-colors duration-300 ${form.volume === t ? "bg-[var(--text-primary)] text-[var(--bg-primary)] border-[var(--text-primary)]" : "border-black/20 text-[var(--text-secondary)] hover:border-black/50"}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">Tell us about your project &amp; theme</label>
                <textarea
                  data-testid="input-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className="mt-2 w-full bg-transparent border-b border-black/20 py-3 text-lg text-[var(--text-primary)] focus:border-[var(--text-primary)] outline-none transition-colors duration-300 resize-none"
                />
              </div>

              <button
                data-testid="enquiry-submit-btn"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 border border-black/20 px-8 h-14 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)] transition-colors duration-500 disabled:opacity-50"
              >
                {loading ? <><CircleNotch size={16} className="animate-spin" /> Sending</> : <>Send Enquiry <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer data-testid="site-footer" className="relative z-10 px-6 md:px-12 lg:px-16 pt-20 pb-10 border-t border-black/10 bg-[var(--bg-secondary)]">
    <div className="flex flex-col md:flex-row justify-between gap-10">
      <div>
        <img src="/emporium-logo.png" alt="Emporium London" className="h-14 w-auto" />
        <p className="mt-5 max-w-xs text-[var(--text-secondary)] leading-relaxed">A curated house of decor, tableware and gifting for hotels, restaurants and corporates.</p>
      </div>
      <div className="grid grid-cols-2 gap-12 font-mono text-[11px] tracking-[0.18em] uppercase">
        <nav aria-label="Footer" className="space-y-3 text-[var(--text-secondary)]">
          <p className="text-[var(--text-muted)]">Explore</p>
          <Link to="/collections" className="block hover:text-[var(--text-primary)] transition-colors">Product Collections</Link>
          <Link to="/products" className="block hover:text-[var(--text-primary)] transition-colors">Our Products</Link>
          <Link to="/bespoke" className="block hover:text-[var(--text-primary)] transition-colors">Bespoke Customisation</Link>
          <Link to="/about" className="block hover:text-[var(--text-primary)] transition-colors">Quality &amp; Service</Link>
          <Link to="/contact" className="block hover:text-[var(--text-primary)] transition-colors">Request a Selection</Link>
        </nav>
        <div className="space-y-3 text-[var(--text-secondary)]">
          <p className="text-[var(--text-muted)]">Connect</p>
          <p>Instagram</p><p>LinkedIn</p><p>Pinterest</p>
        </div>
      </div>
    </div>
    <div className="mt-16 pt-6 border-t border-black/10 flex flex-col sm:flex-row justify-between gap-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">
      <span>© {new Date().getFullYear()} Emporium London — All rights reserved</span>
      <span>Objects &amp; Gifting · London · emporiumlondon.co.uk</span>
    </div>
  </footer>
);
